import apiClient from './apiClient';
import { withActingAs } from './actingAs';
import type { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

/**
 * Append `?acting_as=<id>` so SuperAdmin / AM-with-relation can retry a
 * rejected position on behalf of a target user. Backend authz is enforced
 * by resolve_acting_as in allow_admin=False mode — self-only callers send
 * no param and hit the unchanged code path.
 */
function retryUrl(): string {
  const qs = withActingAs(new URLSearchParams()).toString();
  return qs ? `/positions/entry?${qs}` : '/positions/entry';
}

/**
 * Per-position write actions. The listing endpoint that used to live here
 * (`getPositions`) was retired alongside the standalone /positions page;
 * Dashboard and Schedule's Positions tab now read positions from
 * `schedule_viewer`'s `format=positions` route via scheduleService.
 */
class PositionsService {
  async retryPosition(positionId: string): Promise<RetryResponse> {
    try {
      const response = await apiClient.post<ApiResponse<RetryResponse>>(
        retryUrl(),
        { position_id: positionId },
        undefined,
        API_TYPE
      );

      const wrapped = response as ApiResponse<any>;
      if (wrapped.success === false) {
        throw new Error(wrapped.message || 'Failed to retry position');
      }
      return wrapped.data || response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to retry position');
    }
  }

}

/**
 * Retry response has three shapes (Phase 4/6):
 *   Normal:       { position_id, success, entry_status, error? }
 *   Deferred:     { position_id, deferred: true }
 *                   — user clicked Retry on a PENDING_PLACE row; another
 *                     placement is already in flight. Frontend shows a
 *                     "please wait" toast, does not treat as success/failure.
 *   AlreadyPlaced:{ position_id, already_placed: true, entry_status }
 *                   — user clicked Retry on an ACTIVE/COMPLETE row. Frontend
 *                     shows an informational toast.
 *
 * These fields are mutually exclusive — at most one of success/deferred/
 * already_placed is set on a given response.
 */
interface RetryResponse {
  position_id: string;
  success?: boolean;
  entry_status?: string;
  error?: string | null;
  deferred?: boolean;
  already_placed?: boolean;
}

const positionsService = new PositionsService();
export default positionsService;
export { PositionsService };
